import React, { useState } from "react";
import { IconButton, MenuItem } from "@mui/material";
import { styled } from "@mui/material/styles";
import ClearIcon from "@mui/icons-material/Clear";
import StewartSelect from "controls/global/stewart-select/StewartSelect";
import { colors } from "theme/defaultStyle";

// Single-select column-header filter. The grid's other header widgets are
// InputSearch (free text) and SafeAutocomplete (multi-select with checkboxes);
// this covers the one-of-N case. Modeled on AdvancedSelectField in
// AdvancedSearchPanel, resized to the 40px header row.
const HeaderSelect = styled(StewartSelect)({
  "& .MuiInputBase-root": { height: 40, minHeight: 40 },
  "& .MuiOutlinedInput-root": { height: 40, minHeight: 40 },
  // Leave room for the clear button so the chevron doesn't overlap it.
  "& .MuiInputBase-adornedEnd .MuiSelect-select": { paddingRight: "56px" },
});

export type SelectSearchOption<T> = {
  label: string;
  value: T;
};

interface Props<T> {
  name: string;
  options: SelectSearchOption<T>[];
  disabled?: boolean;
  placeholder?: string;
  onChange: (name: string, value: T | null) => void;
}

// Values are carried by index so non-string option values (booleans, numbers)
// survive the round trip through MUI's string-based Select.
const SelectSearch = <T,>({
  name,
  options,
  disabled,
  placeholder = "Search",
  onChange,
}: Props<T>) => {
  const [selectedIndex, setSelectedIndex] = useState<string>("");
  // Open state has to be controlled: StewartSelect puts pointer-events:auto on
  // the chevron, and the chevron is positioned in the input root's right
  // padding — outside the display div MUI listens on — so relying on MUI's own
  // click handling leaves the arrow dead. The root onClick below covers the
  // whole control instead. onOpen/onClose are still wired so MUI can close the
  // menu itself on selection, Escape and backdrop clicks.
  const [open, setOpen] = useState(false);

  const clearSelection = () => {
    setSelectedIndex("");
    onChange(name, null);
  };

  return (
    <HeaderSelect
      select
      fullWidth
      name={name}
      id={name}
      value={selectedIndex}
      disabled={disabled}
      SelectProps={{
        open,
        onOpen: () => setOpen(true),
        onClose: () => setOpen(false),
        displayEmpty: true,
        renderValue: (value: unknown) => {
          const index = Number(value);
          if (value === "" || Number.isNaN(index)) {
            return <PlaceholderText>{placeholder}</PlaceholderText>;
          }
          return options[index]?.label ?? "";
        },
      }}
      InputProps={{
        endAdornment: selectedIndex !== "" && (
          <IconButton
            aria-label={`Clear ${name}`}
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              clearSelection();
            }}
          >
            <ClearIcon fontSize="small" />
          </IconButton>
        ),
      }}
      onClick={(e: React.MouseEvent) => {
        if (disabled) return;
        // Only ever opens — never toggles. The Menu is portalled in the DOM but
        // is still a React child, so clicks on a menu item, and on the backdrop
        // covering the page while it's open, bubble up here. Toggling on those
        // is what kept the menu open after a selection and swallowed the next
        // click on the page (two clicks needed on Apply Filters). MUI closes it
        // via onClose above.
        if ((e.target as Element).closest?.(".MuiMenu-root")) return;
        setOpen(true);
      }}
      onChange={(e) => {
        const index = Number(e.target.value);
        setSelectedIndex(e.target.value);
        onChange(name, options[index]?.value ?? null);
      }}
    >
      {options.map((option, index) => (
        <MenuItem key={option.label} value={String(index)}>
          {option.label}
        </MenuItem>
      ))}
    </HeaderSelect>
  );
};

const PlaceholderText = styled("span")({
  color: colors.grey06,
});

export default SelectSearch;
